import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaEdit, 
  FaTrash, 
  FaToggleOn, 
  FaToggleOff,
  FaMapMarkerAlt,
  FaBed,
  FaBath,
  FaUsers,
  FaRupeeSign,
  FaStar,
  FaCheckCircle,
  FaHome,
  FaBus,
  FaTrain,
  FaShoppingBag,
  FaLandmark,
  FaQuestionCircle,
  FaSpinner,
  FaTag
} from 'react-icons/fa';
import EditPropertyModal from '../components/EditPropertyModal';
import OfferStatusBadge from '../components/OfferStatusBadge';
import { resolveDiscountStatus } from '../utils/offerStatus';
import { getPropertyImages, getImageUrl } from '../utils/imageHelper';
import { getApiUrl } from '../config/api';
import { toast } from 'react-toastify';
import './HostPropertyDetailsPage.css';

const HostPropertyDetailsPage = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [propertyImages, setPropertyImages] = useState([]);
  const [guestQuestions, setGuestQuestions] = useState([]);
  const [loadingQA, setLoadingQA] = useState(false);
  const [answeringId, setAnsweringId] = useState(null);
  const [answerText, setAnswerText] = useState({});
  const [guestReviews, setGuestReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [replyingReviewId, setReplyingReviewId] = useState(null);
  const [replyText, setReplyText] = useState({});

  useEffect(() => {
    // Check if property was passed via navigation state first
    if (location.state?.property) {
      console.log('✅ Property found in navigation state');
      setProperty(location.state.property);
      setPropertyImages(getPropertyImages(location.state.property));
      setLoading(false);
    } else {
      console.log('⚠️ Property not in navigation state, fetching...');
      fetchProperty();
    }
  }, [propertyId, location.state]);

  // Update images when property changes
  useEffect(() => {
    if (property) {
      setPropertyImages(getPropertyImages(property));
    }
  }, [property]);

  // Fetch all guest questions (for host to answer)
  useEffect(() => {
    if (!propertyId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const fetchQA = async () => {
      setLoadingQA(true);
      try {
        const qaUrl = getApiUrl(`/api/properties/${propertyId}/qa/all`);
        const res = await fetch(qaUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          setGuestQuestions([]);
          return;
        }
        const data = await res.json();
        if (data.success && Array.isArray(data.questions)) {
          setGuestQuestions(data.questions);
        } else {
          setGuestQuestions([]);
        }
      } catch (err) {
        console.error('Error fetching guest questions:', err);
        setGuestQuestions([]);
      } finally {
        setLoadingQA(false);
      }
    };
    fetchQA();
  }, [propertyId]);

  // Fetch guest reviews
  useEffect(() => {
    if (!propertyId) return;
    const fetchReviews = async () => {
      setLoadingReviews(true);
      try {
        const res = await fetch(getApiUrl(`/api/properties/${propertyId}/reviews`));
        const data = await res.json();
        if (data.success && Array.isArray(data.reviews)) {
          setGuestReviews(data.reviews);
        } else {
          setGuestReviews([]);
        }
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setGuestReviews([]);
      } finally {
        setLoadingReviews(false);
      }
    };
    fetchReviews();
  }, [propertyId]);

  const fetchProperty = async () => {
    try {
      const token = localStorage.getItem('token');
      const propertyUrl = getApiUrl(`/api/properties/${propertyId}`);
      const response = await fetch(propertyUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const contentType = response.headers.get('content-type');
      if (!response.ok || !contentType || !contentType.includes('application/json')) {
        if (!response.ok && process.env.NODE_ENV === 'development') {
          console.warn(
            `Property fetch returned ${response.status}. ` +
            'Ensure the API server is running and REACT_APP_API_URL points to it (e.g. http://localhost:5002).'
          );
        }
        // Don't parse; fall through to localStorage fallback
      } else {
        const result = await response.json();
        if (result.success) {
          setProperty(result.property);
          setPropertyImages(getPropertyImages(result.property));
          setLoading(false);
          return;
        }
      }
    } catch (error) {
      console.error('Error fetching property from API:', error);
    }

    // Fallback to localStorage
    try {
      // Check search results
      const searchResults = localStorage.getItem('searchResults');
      if (searchResults) {
        const results = JSON.parse(searchResults);
        const allProperties = results.results || [];
        const foundProperty = allProperties.find(p => 
          (p.id === propertyId || p._id === propertyId)
        );
        if (foundProperty) {
          setProperty(foundProperty);
          setPropertyImages(getPropertyImages(foundProperty));
          setLoading(false);
          return;
        }
      }

      // Check if property was passed via navigation state
      const navigationState = window.history.state?.usr;
      if (navigationState?.property) {
        setProperty(navigationState.property);
        setPropertyImages(getPropertyImages(navigationState.property));
        setLoading(false);
        return;
      }

      // Try to get from host's properties in localStorage
      const user = localStorage.getItem('user');
      if (user) {
        const userObj = JSON.parse(user);
        const userId = userObj._id || userObj.id;
        
        // Check various localStorage keys where properties might be stored
        const possibleKeys = [
          `properties_${userId}`,
          'myProperties',
          'hostProperties'
        ];

        for (const key of possibleKeys) {
          const storedProperties = localStorage.getItem(key);
          if (storedProperties) {
            const properties = JSON.parse(storedProperties);
            const foundProperty = properties.find(p => 
              (p.id === propertyId || p._id === propertyId)
            );
            if (foundProperty) {
              setProperty(foundProperty);
              setPropertyImages(getPropertyImages(foundProperty));
              setLoading(false);
              return;
            }
          }
        }
      }

      console.error('Property not found in any storage');
    } catch (error) {
      console.error('Error fetching property from localStorage:', error);
    }

    setLoading(false);
  };

  const handleToggleStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = getApiUrl(`/api/properties/${propertyId}/toggle-status`);
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        toast.error('Server returned an invalid response. Ensure the API server is running.');
        return;
      }
      const result = await response.json();

      if (result.success) {
        setProperty(result.property);
        toast.success(`Property ${result.property.status === 'active' ? 'activated' : 'deactivated'} successfully!`);
      } else {
        toast.error('Failed to update status: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this property? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const deleteUrl = getApiUrl(`/api/properties/${propertyId}`);
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        toast.error('Server returned an invalid response. Ensure the API server is running.');
        return;
      }
      const result = await response.json();

      if (result.success) {
        toast.success('Property deleted successfully!');
        navigate('/host');
      } else {
        toast.error('Failed to delete property: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error('Failed to delete property');
    }
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handlePropertyUpdated = (updatedProperty) => {
    console.log('✅ Property updated:', updatedProperty);
    
    // Update property state
    setProperty(updatedProperty);
    
    // Update images with the new property images
    const newImages = getPropertyImages(updatedProperty);
    console.log('🖼️ Updated images:', newImages);
    setPropertyImages(newImages);
    
    // Reset image index if needed
    if (currentImageIndex >= newImages.length) {
      setCurrentImageIndex(0);
    }
    
    // Close modal
    setShowEditModal(false);
    
    // Update localStorage if property exists there
    try {
      const searchResults = localStorage.getItem('searchResults');
      if (searchResults) {
        const results = JSON.parse(searchResults);
        const allProperties = results.results || [];
        const updatedProperties = allProperties.map(p => 
          (p.id === propertyId || p._id === propertyId) ? updatedProperty : p
        );
        results.results = updatedProperties;
        localStorage.setItem('searchResults', JSON.stringify(results));
      }
      
      // Also update in host's properties if stored
      const user = localStorage.getItem('user');
      if (user) {
        const userObj = JSON.parse(user);
        const userId = userObj._id || userObj.id;
        const possibleKeys = [
          `properties_${userId}`,
          'myProperties',
          'hostProperties'
        ];
        
        possibleKeys.forEach(key => {
          const storedProperties = localStorage.getItem(key);
          if (storedProperties) {
            const properties = JSON.parse(storedProperties);
            const updatedProperties = properties.map(p => 
              (p.id === propertyId || p._id === propertyId) ? updatedProperty : p
            );
            localStorage.setItem(key, JSON.stringify(updatedProperties));
          }
        });
      }
    } catch (error) {
      console.error('Error updating localStorage:', error);
    }
    
    toast.success('Property updated successfully!');
  };

  const nextImage = () => {
    if (propertyImages.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % propertyImages.length);
    }
  };

  const prevImage = () => {
    if (propertyImages.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + propertyImages.length) % propertyImages.length);
    }
  };

  if (loading) {
    return (
      <div className="host-property-details-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading property...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="host-property-details-page">
        <div className="not-found-container">
          <h2>Property Not Found</h2>
          <button onClick={() => navigate('/host')} className="btn-back">
            <FaArrowLeft /> Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="host-property-details-page">
      {/* Edit Property Modal */}
      <EditPropertyModal
        isOpen={showEditModal}
        property={property}
        onClose={() => setShowEditModal(false)}
        onPropertyUpdated={handlePropertyUpdated}
      />

      {/* Header with Back Button */}
      <div className="host-details-header">
        <button onClick={() => navigate('/host')} className="btn-back-header">
          <FaArrowLeft />
          <span>Back to Properties</span>
        </button>
        
        <div className="header-actions">
          <button 
            className="btn-header-action btn-edit"
            onClick={handleEdit}
            title="Edit Property"
          >
            <FaEdit />
            <span>Edit</span>
          </button>
          <button 
            className="btn-header-action btn-toggle"
            onClick={handleToggleStatus}
            title={property.status === 'active' ? 'Deactivate' : 'Activate'}
          >
            {property.status === 'active' ? <FaToggleOn /> : <FaToggleOff />}
            <span>{property.status === 'active' ? 'Active' : 'Inactive'}</span>
          </button>
          <button 
            className="btn-header-action btn-delete"
            onClick={handleDelete}
            title="Delete Property"
          >
            <FaTrash />
            <span>Delete</span>
          </button>
        </div>
      </div>

      {/* Property Content */}
      <div className="host-details-content">
        {/* Image Gallery */}
        <div className="property-gallery">
          {propertyImages.length > 0 ? (
            <>
              <div className="gallery-main">
                <img 
                  src={propertyImages[currentImageIndex]} 
                  alt={property.name}
                  onError={(e) => {
                    console.error('Image failed to load:', propertyImages[currentImageIndex]);
                    e.target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80';
                  }}
                />
                {propertyImages.length > 1 && (
                  <>
                    <button className="gallery-nav prev" onClick={prevImage}>‹</button>
                    <button className="gallery-nav next" onClick={nextImage}>›</button>
                    <div className="gallery-indicator">
                      {currentImageIndex + 1} / {propertyImages.length}
                    </div>
                  </>
                )}
              </div>
              {propertyImages.length > 1 && (
                <div className="gallery-thumbnails">
                  {propertyImages.map((img, index) => (
                    <img
                      key={index}
                      src={img}
                      alt={`${property.name} ${index + 1}`}
                      className={index === currentImageIndex ? 'active' : ''}
                      onClick={() => setCurrentImageIndex(index)}
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80';
                      }}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="no-image">
              <FaHome size={64} />
              <p>No images available</p>
            </div>
          )}
        </div>

        {/* Property Information */}
        <div className="property-info-section">
          {/* Title and Status */}
          <div className="property-title-row">
            <div>
              <h1>{property.name}</h1>
              <div className="property-location">
                <FaMapMarkerAlt />
                <span>{property.city}</span>
                {property.address && <span className="address-detail">• {property.address}</span>}
              </div>
            </div>
            <div className={`status-badge ${property.status}`}>
              {property.status === 'active' ? (
                <>
                  <FaCheckCircle />
                  <span>Active</span>
                </>
              ) : (
                <>
                  <FaToggleOff />
                  <span>Inactive</span>
                </>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="property-stats">
            <div className="stat-item">
              <FaBed />
              <span>{property.bedrooms || 0} Bedrooms</span>
            </div>
            <div className="stat-item">
              <FaBath />
              <span>{property.bathrooms || 0} Bathrooms</span>
            </div>
            <div className="stat-item">
              <FaUsers />
              <span>{property.maxGuests || 0} Guests</span>
            </div>
            <div className="stat-item">
              <FaRupeeSign />
              <span>₹{property.pricePerNight?.toLocaleString() || 0}/night</span>
            </div>
          </div>

          {/* Property Type */}
          <div className="info-card">
            <h3>Property Type</h3>
            <div className="property-type-badge">{property.type || 'Hotel'}</div>
          </div>

          {/* Description */}
          {property.description && (
            <div className="info-card">
              <h3>Description</h3>
              <p>{property.description}</p>
            </div>
          )}

          {/* Amenities */}
          {property.amenities && property.amenities.length > 0 && (
            <div className="info-card">
              <h3>Amenities ({property.amenities.length})</h3>
              <div className="amenities-grid">
                {property.amenities.map((amenity, index) => (
                  <div key={index} className="amenity-item">
                    <FaCheckCircle />
                    <span>{amenity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Offer / Discount — all statuses visible to owner */}
          {property.discount && (property.discount.title || property.discount.value > 0 || property.discount.isActive) && (
            <div className="info-card offer-card">
              <h3><FaTag className="section-icon" /> Offer &amp; Deal</h3>
              <div className="offer-details">
                <OfferStatusBadge discount={property.discount} />
                <span className="offer-badge">{property.discount.badge || 'Special Offer'}</span>
                {property.discount.validFrom && property.discount.validUntil && (
                  <p className="offer-terms">
                    {new Date(property.discount.validFrom).toLocaleDateString('en-IN')} –{' '}
                    {new Date(property.discount.validUntil).toLocaleDateString('en-IN')}
                  </p>
                )}
                {resolveDiscountStatus(property.discount) === 'expired' && (
                  <p className="offer-terms">This offer is hidden from guests. Edit dates to reactivate.</p>
                )}
                {property.discount.type === 'percentage' && (
                  <p className="offer-value">{property.discount.value}% off</p>
                )}
                {property.discount.type === 'fixed' && (
                  <p className="offer-value">₹{property.discount.value} off</p>
                )}
                {property.discount.title && <p className="offer-title">{property.discount.title}</p>}
                {property.discount.description && <p className="offer-desc">{property.discount.description}</p>}
                {property.discount.minStayNights > 1 && (
                  <p className="offer-terms">Min. stay: {property.discount.minStayNights} nights</p>
                )}
              </div>
            </div>
          )}

          {/* House Rules */}
          {property.houseRules && property.houseRules.length > 0 && (
            <div className="info-card">
              <h3>House Rules ({property.houseRules.length})</h3>
              <div className="rules-list">
                {property.houseRules.map((rule, index) => (
                  <div key={index} className="rule-item">
                    <FaCheckCircle />
                    <span>{rule}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rating */}
          {property.rating && (
            <div className="info-card">
              <h3>Rating</h3>
              <div className="rating-display">
                <FaStar />
                <span className="rating-value">{property.rating}</span>
                {property.reviews && <span className="reviews-count">({property.reviews} reviews)</span>}
              </div>
            </div>
          )}

          {/* Nearby Places */}
          {property.nearbyPlaces && property.nearbyPlaces.length > 0 && (
            <div className="info-card">
              <h3>Nearby Places ({property.nearbyPlaces.length})</h3>
              <div className="nearby-places-list">
                {property.nearbyPlaces.map((place, index) => {
                  // Get icon based on category
                  const getPlaceIcon = (category) => {
                    const cat = category?.toLowerCase() || '';
                    if (cat.includes('bus')) return <FaBus />;
                    if (cat.includes('train') || cat.includes('railway')) return <FaTrain />;
                    if (cat.includes('mall') || cat.includes('shopping')) return <FaShoppingBag />;
                    if (cat.includes('tourist') || cat.includes('landmark')) return <FaLandmark />;
                    return <FaMapMarkerAlt />;
                  };

                  return (
                    <div key={index} className="nearby-place-item">
                      <div className="place-icon">
                        {getPlaceIcon(place.category)}
                      </div>
                      <div className="place-details">
                        <strong>{place.name}</strong>
                        {place.category && <span className="place-category">{place.category}</span>}
                      </div>
                      <span className="place-distance">{place.distance} {place.unit} away</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tourist Attractions */}
          {property.touristPlaces && property.touristPlaces.length > 0 && (
            <div className="info-card">
              <h3>Tourist Attractions Nearby ({property.touristPlaces.length})</h3>
              <div className="tourist-places-grid">
                {property.touristPlaces.map((place, index) => {
                  const touristFallback = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=600&q=80';
                  const imageUrl =
                    place.image && String(place.image).trim()
                      ? getImageUrl(place.image)
                      : touristFallback;

                  return (
                    <div key={index} className="tourist-place-card">
                      <div className="tourist-place-image">
                        <img 
                          src={imageUrl} 
                          alt={place.name || 'Tourist attraction'}
                          loading="lazy"
                          onError={(e) => {
                            e.target.src = touristFallback;
                          }}
                        />
                        <div className="tourist-distance-badge">
                          {place.distance} km
                        </div>
                      </div>
                      <div className="tourist-place-info">
                        <strong>{place.name}</strong>
                        {place.description && <p>{place.description}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Guest Reviews - Host can reply */}
          <div className="info-card host-reviews-section">
            <h3><FaStar className="section-icon" /> Guest Reviews</h3>
            <p className="host-reviews-subtitle">Reply to guest reviews; your reply will appear on the property page.</p>
            {loadingReviews ? (
              <p className="host-reviews-loading"><FaSpinner className="spinner" /> Loading...</p>
            ) : guestReviews.length === 0 ? (
              <p className="host-reviews-empty">No reviews yet.</p>
            ) : (
              <div className="host-reviews-list">
                {guestReviews.map((rev) => (
                  <div key={rev._id} className={`host-review-item ${rev.hostReply ? 'replied' : 'pending'}`}>
                    <div className="host-review-header">
                      <span className="host-review-guest">{rev.guestName || 'Guest'}</span>
                      <div className="host-review-stars">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <FaStar key={s} className={s <= (rev.rating || 0) ? 'star-filled' : 'star-empty'} />
                        ))}
                      </div>
                      <span className="host-review-date">
                        {rev.createdAt ? new Date(rev.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                      </span>
                    </div>
                    {rev.comment && <p className="host-review-comment">{rev.comment}</p>}
                    {rev.hostReply ? (
                      <div className="host-review-reply-display">
                        <strong>Your reply:</strong> {rev.hostReply}
                      </div>
                    ) : (
                      <div className="host-review-reply-form">
                        <textarea
                          placeholder="Type your reply..."
                          value={replyText[rev._id] || ''}
                          onChange={(e) => setReplyText(prev => ({ ...prev, [rev._id]: e.target.value }))}
                          rows={3}
                          maxLength={2000}
                        />
                        <button
                          type="button"
                          className="btn-reply-submit"
                          disabled={!(replyText[rev._id] || '').trim() || replyingReviewId === rev._id}
                          onClick={async () => {
                            const text = (replyText[rev._id] || '').trim();
                            if (!text) return;
                            const token = localStorage.getItem('token');
                            if (!token) return;
                            setReplyingReviewId(rev._id);
                            try {
                              const patchUrl = getApiUrl(`/api/properties/${propertyId}/reviews/${rev._id}`);
                              const res = await fetch(patchUrl, {
                                method: 'PATCH',
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${token}`,
                                },
                                body: JSON.stringify({ reply: text }),
                              });
                              const ct = res.headers.get('content-type');
                              if (!ct || !ct.includes('application/json')) {
                                toast.error('Server returned an invalid response.');
                                return;
                              }
                              const data = await res.json();
                              if (data.success) {
                                setGuestReviews(prev =>
                                  prev.map(r => r._id === rev._id ? { ...r, hostReply: text, hostRepliedAt: new Date() } : r)
                                );
                                setReplyText(prev => ({ ...prev, [rev._id]: '' }));
                              } else {
                                toast.error(data.message || 'Failed to save reply');
                              }
                            } catch (err) {
                              console.error(err);
                              toast.error('Failed to save reply');
                            } finally {
                              setReplyingReviewId(null);
                            }
                          }}
                        >
                          {replyingReviewId === rev._id ? <><FaSpinner className="spinner" /> Saving...</> : 'Submit reply'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Guest Questions & Answers - Host can answer */}
          <div className="info-card host-qa-section">
            <h3><FaQuestionCircle className="section-icon" /> Guest Questions</h3>
            <p className="host-qa-subtitle">Answer guest questions; they will appear on the property page.</p>
            {loadingQA ? (
              <p className="host-qa-loading"><FaSpinner className="spinner" /> Loading...</p>
            ) : guestQuestions.length === 0 ? (
              <p className="host-qa-empty">No questions yet.</p>
            ) : (
              <div className="host-qa-list">
                {guestQuestions.map((qa) => (
                  <div key={qa._id} className={`host-qa-item ${qa.answer && qa.answer.trim() ? 'answered' : 'pending'}`}>
                    <div className="host-qa-question">
                      <FaQuestionCircle />
                      <strong>{qa.question}</strong>
                    </div>
                    {qa.answer && qa.answer.trim() ? (
                      <div className="host-qa-answer"><strong>Your answer:</strong> {qa.answer}</div>
                    ) : (
                      <div className="host-qa-answer-form">
                        <textarea
                          placeholder="Type your answer..."
                          value={answerText[qa._id] || ''}
                          onChange={(e) => setAnswerText(prev => ({ ...prev, [qa._id]: e.target.value }))}
                          rows={3}
                          maxLength={1000}
                        />
                        <button
                          type="button"
                          className="btn-answer-submit"
                          disabled={!(answerText[qa._id] || '').trim() || answeringId === qa._id}
                          onClick={async () => {
                            const text = (answerText[qa._id] || '').trim();
                            if (!text) return;
                            const token = localStorage.getItem('token');
                            if (!token) return;
                            setAnsweringId(qa._id);
                            try {
                              const patchUrl = getApiUrl(`/api/properties/${propertyId}/qa/${qa._id}`);
                              const res = await fetch(patchUrl, {
                                method: 'PATCH',
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${token}`,
                                },
                                body: JSON.stringify({ answer: text }),
                              });
                              const ct = res.headers.get('content-type');
                              if (!ct || !ct.includes('application/json')) {
                                toast.error('Server returned an invalid response. Ensure the API server is running.');
                                return;
                              }
                              const data = await res.json();
                              if (data.success) {
                                setGuestQuestions(prev =>
                                  prev.map(q => q._id === qa._id ? { ...q, answer: text, answeredAt: new Date() } : q)
                                );
                                setAnswerText(prev => ({ ...prev, [qa._id]: '' }));
                              } else {
                                toast.error(data.message || 'Failed to save answer');
                              }
                            } catch (err) {
                              console.error(err);
                              toast.error('Failed to save answer');
                            } finally {
                              setAnsweringId(null);
                            }
                          }}
                        >
                          {answeringId === qa._id ? <><FaSpinner className="spinner" /> Saving...</> : 'Submit answer'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HostPropertyDetailsPage;
